import { useIonViewWillEnter } from '@ionic/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LegacyProductCode,
  Permission,
  ProductsApi,
} from '@up2stay/up2promo-api-client';
import {
  formValuesFromProduct,
  getPhysicalFormLabel,
  getPhysicalMaterialLabel,
  getProductFormRoutingState,
  getProductKindLabel,
  intendedBehaviorToProductType,
  mapLocationsForForm,
  parseInstagramUsername,
  parseTiktokUsername,
  resolveBehaviorProductType,
  resolvePhysicalForm,
  resolveSummaryRoutingMode,
} from '@up2stay/up2promo-commons/products';
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Skeleton,
} from '@up2stay/up2promo-ui';
import { Trash2 } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';
import { matchPath, useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AppIonPage } from '@/core/components/app-ion-page';
import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { PageHeader } from '@/core/components/page-header';
import { PhysicalFormTile } from '@/core/components/products/physical-form-icons';
import { ProductBehaviorChip } from '@/core/components/products/product-behavior-chip';
import { ProductFormFields } from '@/core/components/products/product-form-fields';
import { ProductHashChip } from '@/core/components/products/product-hash-chip';
import { ProductHistorySection } from '@/core/components/products/product-history-section';
import {
  SettingsGroup,
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsSection } from '@/core/components/settings/settings-section';
import { AutosaveStatusCue } from '@/core/components/ui/autosave-status-cue';
import { useProduct } from '@/core/hooks/use-product';
import { useLocations } from '@/core/hooks/use-locations';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useProductDetailForm } from '@/core/hooks/use-product-detail-form';
import { useResolvedWorkspaceMember } from '@/core/hooks/use-workspace-member-picker';
import {
  goBackOrFallback,
  useIonBackRouter,
} from '@/core/lib/ion-back-navigation';
import { workspaceMemberDisplayName } from '@/core/lib/workspace-member-display';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { productsQueryKeys } from '@/core/query-keys/products-query-keys';
import { shellPaths } from '@/core/router/app-paths';
import { isShellObjectIdParam } from '@/core/router/shell-object-id-param';
import { useAuthStore } from '@/core/store/auth.store';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

import type { ProductsDict } from './products';

function ProductDetailBreadcrumb({
  workLabel,
  productsLabel,
}: {
  workLabel: string;
  productsLabel: string;
}) {
  return (
    <>
      {workLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {productsLabel}
    </>
  );
}

function ProductDetailPageShell({
  children,
  isSplitLayout,
}: {
  children: ReactNode;
  isSplitLayout: boolean;
}) {
  return (
    <AppPageContent
      scrollY={!isSplitLayout}
      className={isSplitLayout ? 'flex h-full min-h-0 flex-col' : undefined}
    >
      <AppPageLayout
        className={
          isSplitLayout
            ? 'min-h-0 flex-1 overflow-hidden pb-6'
            : 'min-w-0 overflow-x-hidden md:pb-6'
        }
      >
        {children}
      </AppPageLayout>
    </AppPageContent>
  );
}

const PRODUCT_DETAIL_PATH = `${shellPaths.moreProducts}/:productId`;

function resolveProductId(
  params: { productId?: string },
  pathname: string,
): string | undefined {
  if (isShellObjectIdParam(params.productId)) {
    return params.productId;
  }

  const matched = matchPath({ path: PRODUCT_DETAIL_PATH, end: true }, pathname);

  const productId = matched?.params?.productId;
  return isShellObjectIdParam(productId) ? productId : undefined;
}

/**
 * This URL is a product detail whose id can never load ("abc"), as opposed to
 * a static sibling like /products/setup. It used to stay in the skeleton
 * forever because no request was ever sent (DEV-558).
 */
function isMalformedProductDetailPath(pathname: string): boolean {
  if (pathname === shellPaths.moreProductsSetup) return false;
  const matched = matchPath({ path: PRODUCT_DETAIL_PATH, end: true }, pathname);
  const productId = matched?.params?.productId;
  return !!productId && !isShellObjectIdParam(productId);
}

export function ProductDetailPage() {
  const routeParams = useParams<{ productId: string }>();
  const location = useLocation();
  const productId = resolveProductId(routeParams, location.pathname);
  const isMobile = useIsMobile();
  const isSplitLayout = !isMobile;
  const backRouter = useIonBackRouter();
  const goBackFromDetail = (fallback: string) =>
    goBackOrFallback(backRouter, fallback);
  const dict = useDictionary();
  const queryClient = useQueryClient();
  const currentWorkspace = useCurrentWorkspace();
  const currentUserId = useAuthStore((state) => state.user?.id);
  const { can } = usePermissions();
  const canManageProducts = can(Permission.PRODUCTS_MANAGE);
  const productsDict = (dict as { products?: ProductsDict }).products;
  const detailDict = productsDict?.detail;

  const { product, isLoading, isPending, isFetched, error, refetch } =
    useProduct(productId);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useIonViewWillEnter(() => {
    void refetch();
  });

  const { locations: rawLocations } = useLocations();
  const locations = useMemo(
    () => mapLocationsForForm(rawLocations),
    [rawLocations],
  );

  // The detail form never locks routing to a printed short code: the
  // product's target kind is the source of truth (the pre-v2 `productType`
  // lock has not been rendered since migration 020; ADR-0026).
  const knownProductType: string | undefined = undefined;

  const formMessages = useMemo(
    () => ({
      descriptionRequired: productsDict?.setup?.descriptionRequired,
      targetUrlRequired: productsDict?.edit?.targetUrlRequired,
      invalidTargetUrl: productsDict?.edit?.invalidTargetUrl,
      feedbackFormRequired:
        productsDict?.edit?.feedbackFunnel?.feedbackFormRequired,
      saveError: detailDict?.saveError,
      genericError: productsDict?.edit?.error,
      statsResetPrompt: detailDict?.statsResetPrompt,
      statsResetAction: detailDict?.statsResetAction,
      statsResetDone: detailDict?.statsResetDone,
      statsResetError: detailDict?.statsResetError,
    }),
    [
      productsDict?.setup?.descriptionRequired,
      productsDict?.edit?.targetUrlRequired,
      productsDict?.edit?.invalidTargetUrl,
      productsDict?.edit?.feedbackFunnel?.feedbackFormRequired,
      productsDict?.edit?.error,
      detailDict?.saveError,
      detailDict?.statsResetPrompt,
      detailDict?.statsResetAction,
      detailDict?.statsResetDone,
      detailDict?.statsResetError,
    ],
  );

  const {
    values: formValues,
    isFormSynced,
    autosaveStatus,
    saveError,
    fieldErrors,
    patchValues,
    setRoutingMode,
    onTargetUrlBlur,
    onDescriptionBlur,
  } = useProductDetailForm({
    product,
    productId,
    workspaceId: currentWorkspace?.id,
    knownProductType,
    locations,
    messages: formMessages,
  });

  const { data: profileMember } = useResolvedWorkspaceMember(
    currentWorkspace?.id,
    formValues.userId,
  );

  const locationIdToName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const loc of locations) {
      map[loc.id] = loc.name ?? loc.id;
    }
    return map;
  }, [locations]);

  const readyProduct =
    product?.id && productId && product.id === productId ? product : null;
  /**
   * Mirrors backend `assertMemberCanModifyMapping`: releasing a card needs
   * `products:manage`, or the card must be assigned to the current user.
   */
  const canDeleteProduct =
    canManageProducts ||
    (!!readyProduct?.target?.userId &&
      readyProduct.target.userId === currentUserId);
  const summaryFormValues =
    isFormSynced || !readyProduct
      ? formValues
      : formValuesFromProduct(readyProduct);

  const routingSummary = useMemo(() => {
    if (!summaryFormValues || !product) return null;
    const routing = getProductFormRoutingState({
      values: summaryFormValues,
      knownProductType,
      locations,
      kindLabels: productsDict?.edit,
      routingModeOverride: formValues.routingMode,
    });
    const mode = resolveSummaryRoutingMode(
      routing.routingMode,
      summaryFormValues.targetKind,
    );
    if (mode === 'profile') {
      if (!summaryFormValues.userId) return '—';
      if (profileMember) {
        return workspaceMemberDisplayName(profileMember);
      }
      return detailDict?.profile || '—';
    }
    if (mode === 'location' && summaryFormValues.locationId) {
      return (
        locationIdToName[summaryFormValues.locationId] ??
        summaryFormValues.locationId
      );
    }
    if (mode === 'instagramUsername') {
      const u = parseInstagramUsername(summaryFormValues.targetUrl);
      return u ? `@${u.replace(/^@/, '')}` : summaryFormValues.targetUrl || '—';
    }
    if (mode === 'tiktokUsername') {
      const u = parseTiktokUsername(summaryFormValues.targetUrl);
      return u ? `@${u.replace(/^@/, '')}` : summaryFormValues.targetUrl || '—';
    }
    if (mode === 'customUrl') {
      return summaryFormValues.targetUrl || '—';
    }
    // Location without a location yet: a stale URL is not the target.
    return '—';
  }, [
    summaryFormValues,
    product,
    knownProductType,
    locations,
    productsDict,
    profileMember,
    locationIdToName,
    detailDict?.profile,
    formValues.routingMode,
  ]);

  const { data: scans30d = 0, isLoading: scansLoading } = useQuery({
    queryKey: ['product-scans-30d', currentWorkspace?.id, product?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id || !product?.id) return 0;
      const res = await ProductsApi.findScans({
        path: { workspaceId: currentWorkspace.id, id: product.id },
        query: { days: 30 },
      });
      return res.data?.total ?? 0;
    },
    enabled: !!currentWorkspace?.id && !!product?.id,
    staleTime: 60 * 1000,
  });

  const removeProduct = useMutation({
    mutationFn: async () => {
      if (!currentWorkspace || !product?.id) throw new Error('Missing data');
      return ProductsApi.remove({
        path: { workspaceId: currentWorkspace.id, id: product.id },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: productsQueryKeys.root,
      });
      toast.success(productsDict?.removeSuccess);
      goBackFromDetail(shellPaths.moreProducts);
    },
    onError: () => {
      toast.error(productsDict?.removeError);
    },
  });

  const hasMalformedProductId =
    !productId && isMalformedProductDetailPath(location.pathname);
  const isLoadingProduct =
    !hasMalformedProductId &&
    (!productId || isPending || isLoading || (!!productId && !isFetched));
  const isProductMissing =
    hasMalformedProductId ||
    (!!productId && isFetched && !isPending && !isLoading && !readyProduct);

  const pageTitle =
    formValues.description?.trim() ||
    readyProduct?.label?.trim() ||
    readyProduct?.hash ||
    detailDict?.title ||
    productsDict?.title ||
    '';

  // The chip follows the current redirect, live while editing, not the
  // product's factory intent (DEV-572, decision Marvin).
  const behaviorProductType = readyProduct
    ? (intendedBehaviorToProductType(String(summaryFormValues.targetKind)) ??
      resolveBehaviorProductType(readyProduct))
    : undefined;
  const productKindLabel = readyProduct
    ? getProductKindLabel(behaviorProductType, productsDict?.edit)
    : '—';
  const physicalForm = readyProduct
    ? resolvePhysicalForm(readyProduct)
    : undefined;
  const physicalFormLabel = getPhysicalFormLabel(
    physicalForm,
    productsDict?.identity,
  );

  const physicalIdentityChips = useMemo(() => {
    if (!readyProduct) return [];
    const chips: string[] = [];
    if (physicalForm) {
      chips.push(physicalFormLabel);
    }
    const materialLabel = getPhysicalMaterialLabel(
      readyProduct.item.material,
      productsDict?.identity,
    );
    if (materialLabel) chips.push(materialLabel);
    const size = readyProduct.item.size?.trim();
    if (size) chips.push(size);
    const color = readyProduct.item.color?.trim();
    if (color) chips.push(color);
    const locale = readyProduct.item.language?.trim();
    if (locale) chips.push(locale.toUpperCase());
    return chips;
  }, [readyProduct, productsDict, physicalForm, physicalFormLabel]);

  const breadcrumb = (
    <ProductDetailBreadcrumb
      workLabel={dict.nav.navDomainWork}
      productsLabel={dict.nav.products}
    />
  );

  const deleteAction =
    readyProduct && canDeleteProduct ? (
      <Button
        variant="outline"
        size="md"
        className="gap-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
        onClick={() => setShowDeleteDialog(true)}
        aria-label={detailDict?.deleteButton ?? 'Delete'}
      >
        <Trash2 className="size-4" />
        {detailDict?.deleteButton ?? 'Delete'}
      </Button>
    ) : undefined;

  const layoutGridClass =
    'grid w-full min-w-0 grid-cols-1 items-start gap-4 md:grid-cols-[minmax(0,1fr)_minmax(240px,18rem)] md:gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(260px,20rem)] lg:gap-[26px] xl:grid-cols-[minmax(0,1fr)_minmax(280px,22rem)] 2xl:grid-cols-[minmax(0,1fr)_minmax(300px,24rem)]';

  const mainContent =
    isProductMissing || error ? (
      <SettingsGroup>
        <SettingsGroupRows>
          <SettingsGroupRow>
            <div className="flex flex-col items-center gap-4 py-4 text-center sm:py-6">
              <p className="text-[15.5px] font-bold leading-tight text-foreground">
                {detailDict?.notFound}
              </p>
              <p className="max-w-prose text-[13.5px] leading-relaxed text-muted-foreground">
                {detailDict?.notFoundDescription}
              </p>
              <Button
                variant="outline"
                onClick={() => goBackFromDetail(shellPaths.moreProducts)}
              >
                {productsDict?.setup?.goToProducts}
              </Button>
            </div>
          </SettingsGroupRow>
        </SettingsGroupRows>
      </SettingsGroup>
    ) : isLoadingProduct ? (
      <SettingsGroup>
        <SettingsGroupRows>
          <SettingsGroupRow>
            <div className="flex flex-col gap-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          </SettingsGroupRow>
        </SettingsGroupRows>
      </SettingsGroup>
    ) : (
      <div className="flex min-w-0 flex-col gap-6">
        {isFormSynced ? (
          <ProductFormFields
            key={productId}
            values={formValues}
            onChange={patchValues}
            knownProductType={knownProductType}
            workspaceId={currentWorkspace?.id}
            locations={locations}
            dict={productsDict}
            idPrefix="detail"
            routingMode={formValues.routingMode ?? 'customUrl'}
            onRoutingModeChange={setRoutingMode}
            fieldErrors={fieldErrors}
            onTargetUrlBlur={onTargetUrlBlur}
            onDescriptionBlur={onDescriptionBlur}
            leading={
              <div className="flex flex-col gap-3">
                <AutosaveStatusCue status={autosaveStatus} />
                {readyProduct?.hash ? (
                  <ProductHashChip hash={readyProduct.hash} />
                ) : null}
              </div>
            }
          />
        ) : (
          <SettingsGroup>
            <SettingsGroupRows>
              <SettingsGroupRow>
                <div className="flex flex-col gap-4">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-32 w-full" />
                </div>
              </SettingsGroupRow>
            </SettingsGroupRows>
          </SettingsGroup>
        )}
        {saveError ? (
          <Alert variant="destructive">
            <AlertDescription>{saveError}</AlertDescription>
          </Alert>
        ) : null}
      </div>
    );

  const propertiesPanel = readyProduct ? (
    <SettingsSection
      id="product-properties"
      title={detailDict?.properties ?? 'Properties'}
      info={scans30d > 0 ? detailDict?.scans30dHint : undefined}
      grouped
      className="h-fit"
    >
      <SettingsGroupRows>
        <SettingsGroupRow>
          <div className="flex items-start gap-3">
            <PhysicalFormTile form={physicalForm} label={physicalFormLabel} />
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <ProductBehaviorChip
                productType={behaviorProductType}
                label={productKindLabel}
              />
            </div>
          </div>
        </SettingsGroupRow>
        {physicalIdentityChips.length > 0 ? (
          <SettingsGroupRow separated>
            <div className="flex flex-col gap-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
                {productsDict?.identity?.physicalIdentity ?? 'Physical product'}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {physicalIdentityChips.map((chip) => (
                  <Badge key={chip} variant="secondary" className="font-medium">
                    {chip}
                  </Badge>
                ))}
              </div>
            </div>
          </SettingsGroupRow>
        ) : null}
        <SettingsGroupRow separated>
          <div className="flex flex-col gap-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
              {detailDict?.redirectTarget ?? detailDict?.behaviour}
            </p>
            <p className="break-all text-[13.5px] font-medium leading-relaxed text-foreground">
              {routingSummary}
            </p>
          </div>
        </SettingsGroupRow>
        <SettingsGroupRow separated>
          <div className="flex flex-col gap-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
              {detailDict?.scans30d}
            </p>
            <div className="text-[13.5px] font-medium tabular-nums leading-relaxed text-foreground">
              {scansLoading ? (
                <Skeleton className="h-5 w-12" />
              ) : scans30d > 0 ? (
                scans30d
              ) : (
                <span className="text-muted-foreground">
                  {detailDict?.noScans}
                </span>
              )}
            </div>
          </div>
        </SettingsGroupRow>
      </SettingsGroupRows>
    </SettingsSection>
  ) : isLoadingProduct ? (
    <SettingsGroup className="hidden h-fit md:block">
      <SettingsGroupRows>
        <SettingsGroupRow>
          <div className="flex flex-col gap-3">
            <Skeleton className="h-5 w-28" />
            <Skeleton className="h-40 w-full" />
          </div>
        </SettingsGroupRow>
      </SettingsGroupRows>
    </SettingsGroup>
  ) : null;

  const historyPanel =
    readyProduct && productId ? (
      <ProductHistorySection
        workspaceId={currentWorkspace?.id}
        productId={productId}
        dict={detailDict?.history}
        kindLabels={productsDict?.edit}
        locationNames={locationIdToName}
      />
    ) : null;

  return (
    <AppIonPage key={productId}>
      <ProductDetailPageShell isSplitLayout={isSplitLayout}>
        <PageHeader
          title={pageTitle}
          breadcrumb={breadcrumb}
          backHref={shellPaths.moreProducts}
          onBack={() => goBackFromDetail(shellPaths.moreProducts)}
          actions={deleteAction}
        />
        {isSplitLayout ? (
          <div className={`${layoutGridClass} min-h-0 flex-1`}>
            <div className="min-h-0 overflow-y-auto pr-1">{mainContent}</div>
            <div className="flex min-h-0 flex-col gap-6 overflow-y-auto">
              {propertiesPanel}
              {historyPanel}
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4 md:gap-5">
            {mainContent}
            {propertiesPanel}
            {historyPanel}
          </div>
        )}
      </ProductDetailPageShell>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {detailDict?.deleteTitle ?? productsDict?.removeConfirmTitle}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {detailDict?.deleteDescription ??
                productsDict?.removeConfirmMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {detailDict?.deleteCancel ?? productsDict?.setup?.cancel}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => removeProduct.mutate()}
              disabled={removeProduct.isPending}
            >
              {removeProduct.isPending
                ? productsDict?.removing
                : (detailDict?.deleteConfirm ?? productsDict?.removeProduct)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppIonPage>
  );
}
